import { isAxiosError } from 'axios'
import { useIntl } from 'react-intl'
import { getTheme } from '../config'
import { addSpacesToPascalCase } from '../services/helpers'

interface ApiErrorData {
	message?: string
	errors?: Array<{ errorMessage?: string; propertyName?: string }>
}

interface ErrorResponse {
	message: string
	errors: string[]
}

const theme = getTheme()

// Map of 4xx status codes to their default error message IDs
const CLIENT_ERROR_MESSAGES: Record<number, string> = {
	400: 'errors.invalid_request',
	401: 'errors.session_expired',
	403: 'errors.no_permission',
	404: 'errors.resource_not_found',
	409: 'errors.request_conflict',
	422: 'errors.invalid_input',
	429: 'errors.too_many_requests',
}

// Type guard for strings
const isNonEmptyString = (msg: unknown): msg is string =>
	typeof msg === 'string' && msg.trim().length > 0

// Extract multiple error messages from ApiErrorData
const extractErrorMessages = (data: ApiErrorData): string[] => {
	if (!data?.errors?.length) return []
	return data.errors
		.map((err) => {
			const { propertyName, errorMessage } = err
			return propertyName
				? `${addSpacesToPascalCase(propertyName)}: ${errorMessage}`
				: err.errorMessage
		})
		.filter(isNonEmptyString)
}

/**
 * Handles errors from API requests or other sources.
 * @param errorMessages - List of extracted error messages
 * @param data - The raw ApiErrorData object
 * @param defaultMessageId - Default i18n ID if no messages exist
 * @param formatMessage - Intl formatter
 * @returns Combined error message string
 */
const buildClientErrorMessage = (
	errorMessages: string[],
	data: ApiErrorData,
	defaultMessageId: string,
	formatMessage: (descriptor: { id: string }) => string
): string => {
	if (errorMessages.length > 0) {
		return errorMessages.length > 1
			? errorMessages.join('. ')
			: errorMessages[0]
	}
	return data?.message ?? formatMessage({ id: defaultMessageId })
}

/**
 * Error Handling Priority:
 * 1. ERR_NETWORK - Network connectivity issues (no fallback override)
 * 2. ECONNABORTED/timeout - Request timeouts (no fallback override)
 * 3. !error.response - CORS, cancellation, invalid URLs (uses fallback if provided)
 * 4. 4xx status - Client errors with backend messages (no fallback override)
 * 5. 5xx status - Server errors (uses fallback if provided)
 * 6. JavaScript Error - Runtime errors (uses fallback if provided)
 * 7. String error - Plain string errors (no fallback override)
 * 8. Ultimate fallback - Unknown error types (uses fallback if provided)
 *
 * @returns Object containing handleError function
 */
const useErrorHandler = () => {
	const { formatMessage } = useIntl()

	/**
	 * Handles errors from API requests or other sources.
	 * @param error - The error object (e.g., AxiosError, JavaScript Error, or string)
	 * @param fallbackMessage - Optional custom fallback message
	 * @returns An ErrorResponse object with a combined message and optional error list
	 */
	const handleError = (
		error: unknown,
		fallbackMessage?: string
	): ErrorResponse => {
		// Handle Axios HTTP errors
		if (isAxiosError(error)) {
			const status = error.response?.status
			const data = error.response?.data as ApiErrorData

			// Network errors (no response received)
			if (error.code === 'ERR_NETWORK') {
				return {
					message: formatMessage({ id: 'errors.unable_to_connect' }),
					errors: [],
				}
			}

			// Timeout errors
			if (
				error.code === 'ECONNABORTED' ||
				error.message?.includes('timeout')
			) {
				return {
					message: formatMessage({ id: 'errors.request_timeout' }),
					errors: [],
				}
			}

			// Other scenarios with no response
			if (!error.response) {
				return {
					message:
						fallbackMessage ??
						formatMessage({ id: 'errors.unexpected_error_short' }),
					errors: [],
				}
			}

			// Client errors (4xx)
			if (status && status >= 400 && status < 500) {
				const errorMessages = extractErrorMessages(data)
				const defaultMessageId =
					CLIENT_ERROR_MESSAGES[status] || 'errors.request_issue'

				const message = buildClientErrorMessage(
					errorMessages,
					data,
					defaultMessageId,
					formatMessage
				)

				return {
					message,
					errors: errorMessages,
				}
			}

			// Server errors (5xx)
			if (status && status >= 500) {
				return {
					message: fallbackMessage ?? theme.api_messages.server_error,
					errors: [],
				}
			}
		}

		// Non-HTTP errors (JavaScript errors, etc.)
		if (error instanceof Error) {
			return {
				message:
					fallbackMessage ??
					formatMessage({ id: 'errors.unexpected_error_short' }),
				errors: [],
			}
		}

		if (typeof error === 'string') {
			return {
				message: error,
				errors: [],
			}
		}

		// Ultimate fallback
		return {
			message:
				fallbackMessage ??
				formatMessage({ id: 'errors.unexpected_error_retry' }),
			errors: [],
		}
	}

	return { handleError }
}

export default useErrorHandler
